import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClientPortal } from '@/components/ClientPortal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Loader2, AlertCircle, LogIn } from 'lucide-react';

const ClientPortalPage = () => {
  const { sessionToken } = useParams<{ sessionToken: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientEmail, setClientEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateSession = async () => {
      if (!sessionToken) {
        setError('Invalid session token');
        setLoading(false);
        return;
      }

      // Wait for auth to load
      if (authLoading) return;

      // If not authenticated, store token and redirect to auth
      if (!user) {
        localStorage.setItem('pending_client_portal_session', sessionToken);
        navigate('/auth?redirect=/client-dashboard');
        return;
      }

      try {
        // Validate session token
        const { data: validationResult, error: validationError } = await supabase
          .rpc('validate_client_portal_session', { session_token_input: sessionToken });

        if (validationError) {
          console.error('Session validation error:', validationError);
          setError('This portal link is no longer valid. Please contact your event planner for a new link.');
          setLoading(false);
          return;
        }

        if (!validationResult || validationResult.length === 0) {
          setError('This portal link is no longer valid. Please contact your event planner for a new link.');
          setLoading(false);
          return;
        }

        const session = validationResult[0];
        
        if (!session.is_valid) {
          setError('This portal link has expired. Please contact your event planner for a new link.');
          setLoading(false);
          return;
        }

        // Get client info using SECURITY DEFINER function (bypasses RLS)
        const { data: clientDataArray, error: clientError } = await supabase
          .rpc('get_client_for_invitation', { p_client_id: session.client_id });

        if (clientError || !clientDataArray || clientDataArray.length === 0) {
          setError('Client not found. Please contact your event planner.');
          setLoading(false);
          return;
        }

        const clientData = clientDataArray[0];

        setClientEmail(clientData.email);

        // Check if user email matches client email
        if (clientData.email && user.email?.toLowerCase() !== clientData.email.toLowerCase()) {
          setError(`This portal is for ${clientData.email}. You are logged in as ${user.email}. Please sign out and log in with the correct email.`);
          setLoading(false);
          return;
        }

        // Check if user is already a client team member
        const { data: existingMember } = await supabase
          .from('client_team_members')
          .select('id, role')
          .eq('client_id', session.client_id)
          .eq('user_id', user.id)
          .single();

        // If not already a member, create client membership
        if (!existingMember) {
          const { error: memberError } = await supabase
            .from('client_team_members')
            .insert({
              client_id: session.client_id,
              user_id: user.id,
              email: user.email || '',
              name: user.user_metadata?.display_name || user.email?.split('@')[0] || 'Client',
              role: 'client',
              is_active: true,
              joined_at: new Date().toISOString()
            });

          if (memberError) {
            console.error('Error creating client membership:', memberError);
            // Continue anyway if it's a duplicate
            if (!memberError.message.includes('duplicate')) {
              setError('Failed to link your account to the client portal');
              setLoading(false);
              return;
            }
          } else {
            toast.success(`You have been added as a client for ${clientData.name}`);
          }
        }

        setClientId(session.client_id);
      } catch (err) {
        console.error('Error validating session:', err);
        setError('Failed to validate session');
        toast.error('Failed to access client portal');
      } finally {
        setLoading(false);
      }
    };

    validateSession();
  }, [sessionToken, user, authLoading, navigate]);

  // Handle sign out for email mismatch
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    localStorage.setItem('pending_client_portal_session', sessionToken || '');
    navigate('/auth');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Accessing Client Portal</h2>
            <p className="text-muted-foreground">Please wait while we validate your session...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    const isEmailMismatch = error.includes('You are logged in as');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold text-destructive">Access Denied</h2>
            <p className="text-muted-foreground">{error}</p>
            
            {isEmailMismatch ? (
              <div className="space-y-3 pt-4">
                <Button onClick={handleSignOut} className="w-full">
                  <LogIn className="h-4 w-4 mr-2" />
                  Sign Out & Login with Correct Email
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Please contact your event planner for a new access link.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Client Found</h2>
            <p className="text-muted-foreground">
              Unable to load client information. Please contact your event planner.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10">
      <ClientPortal clientId={clientId} sessionToken={sessionToken} />
    </div>
  );
};

export default ClientPortalPage;
